import React, { useState, Suspense } from "react"
import { Field, CheckboxField } from "core/form"
import { OldButton } from "@kit/design/old-button"
import Input from "@kit/design/input"
import Slider from "@kit/design/slider"
import Grid from "@kit/design/grid"
import Stack from "@kit/design/stack"
import FileKit from "@convertkit/file-kit"
import { Alignment } from "core/alignment"
import { FileGallery } from "core/file-gallery"
import { useToggle } from "hooks/use-toggle"
import { useCurrentUser } from "hooks/use-current-user"
import { LinkTriggerForm } from "app/editor/plugins/link-triggers/form"

const DokaModal = React.lazy(() => import("vendor/lazy-doka.js"))

function ImageEditorButton({ src, onChange }) {
  const [active, toggle] = useToggle()

  const handleChange = output => {
    onChange(output)
    toggle(false)
  }

  if (!src) return null

  return (
    <>
      <OldButton is="secondary" onClick={toggle}>
        Edit image
      </OldButton>
      <Suspense fallback={null}>
        {active && (
          <DokaModal
            style={{ maxWidth: "200px", height: "200px" }}
            src={src}
            onConfirm={handleChange}
            onCancel={toggle}
          />
        )}
      </Suspense>
    </>
  )
}

export const ImageMenu = ({ editor, node }) => {
  const user = useCurrentUser()
  const target = editor.props.target

  const filekitApiDomain = new URL(process.env.FILEKIT_HOST).hostname
  const filekitDomain = filekitApiDomain.split(".").slice(-2).join(".")

  const parent = editor.value.document.getParent(node.key)

  const block = parent?.type === "captioned-image" ? parent : node

  const imageNode = node

  const [active, setActive] = useState(false)

  const {
    optimize = true,
    borderRadius = 0,
    alt = "",
    src = ""
  } = imageNode.data.toJS()

  const {
    alignment = "center",
    wrapText = false,
    margin = 0,
    width = 800
  } = block.data.toJS()

  const availableAlignments = wrapText
    ? ["left", "right"]
    : ["left", "center", "right"]

  const setImageData = event => {
    const { name, value } = event.target
    editor.command("setNodeByKey", imageNode.key, {
      data: { ...imageNode.data.toJS(), [name]: value }
    })
  }

  const setParentData = event => {
    const { name, value } = event.target
    editor.command("setNodeByKey", block.key, {
      data: { ...block.data.toJS(), [name]: value }
    })
  }

  const setImageAndParentData = event => {
    setImageData(event)
    setParentData(event)
  }

  const setAlignment = ({ name, value }) => {
    setParentData({ target: { name, value } })
  }

  const handleWrapChange = event => {
    const checked = event.target.checked

    const nodeData = { ...block.data.toJS() }

    if (checked) {
      if (margin == 0) {
        nodeData.margin = "15"
      }

      if (alignment === "center") {
        nodeData.alignment = "left"
      }
    }

    nodeData.wrapText = checked

    editor.command("setNodeByKey", block.key, {
      data: nodeData
    })
  }

  const setOptimization = event => {
    const { name, checked } = event.target
    editor.command("setNodeByKey", imageNode.key, {
      data: { ...imageNode.data.toJS(), [name]: checked }
    })
  }

  const onKeyDown = event => {
    if (event.key === "Enter") event.preventDefault()
  }

  const handleEditConfirm = async output => {
    const filekit = FileKit({
      authURL: "/filekit/sessions",
      filekitURL: process.env.FILEKIT_HOST,
      id: user.account_id
    })
    const parentPath = FileKit.pathFromURL(src)
    const result = await filekit.upload(output.file, parentPath)

    editor.command("setNodeByKey", imageNode.key, {
      data: { ...imageNode.data.toJS, ["src"]: result.original_url }
    })
  }

  const closeFileGallery = upload => {
    setActive(false)

    if (!upload?.dataURL) {
      return
    }

    editor.command("setNodeByKey", imageNode.key, {
      data: { ...imageNode.data.toJS(), ["src"]: upload.dataURL }
    })
  }

  return (
    <Stack spacing={1}>
      <Field
        control={Input}
        label="Alt text"
        name="alt"
        value={alt}
        onChange={setImageData}
        onKeyDown={onKeyDown}
      />
      {target === "web" && (
        <div className="pt-2 flex flex-row space-x-2">
          <ImageEditorButton src={src} onChange={handleEditConfirm} />
          <OldButton
            is="secondary"
            onClick={() => {
              setActive(true)
            }}
          >
            Replace image
          </OldButton>
        </div>
      )}
      <LinkTriggerForm editor={editor} node={node} />
      <Grid columns="repeat(2, 1fr)" gap=".5rem" rowGap=".75rem">
        <Field
          control={Slider}
          label="Border Radius"
          name="borderRadius"
          min={0}
          max={100}
          value={borderRadius}
          onChange={setImageData}
        />

        <Field
          control={Slider}
          label="Margin"
          name="margin"
          min={0}
          max={40}
          value={margin}
          onChange={setParentData}
        />
      </Grid>
      <Grid columns="repeat(2, 1fr)" gap=".5rem" rowGap=".75rem">
        <Field
          control={Input}
          label="Width"
          type="number"
          name="width"
          min={0}
          max={800}
          value={width}
          onChange={setImageAndParentData}
        />

        <Stack spacing={2}>
          <div className="inline-block text-xs font-semibold leading-snug text-gray-800 mb-1">
            Alignment
          </div>
          <Alignment
            alignment={alignment}
            onChange={({ name, value }) => setAlignment({ name, value })}
            availableAlignments={availableAlignments}
          />
        </Stack>

        {target !== "web" && (
          <CheckboxField
            label="Wrap text"
            onChange={handleWrapChange}
            name="wrapText"
            checked={wrapText}
          />
        )}
      </Grid>
      <div className="pt-2">
        {src?.includes(filekitDomain) && (
          <>
            <CheckboxField
              label="Optimize image"
              onChange={setOptimization}
              name="optimize"
              checked={optimize}
            />
            <p className="subtext text-xs pt-1">
              Improves loading time for your page
            </p>
          </>
        )}
      </div>
      {target === "email" && (
        <div className="pt-2 flex flex-row space-x-2">
          <ImageEditorButton src={src} onChange={handleEditConfirm} />
          <OldButton
            is="secondary"
            onClick={() => {
              setActive(true)
            }}
          >
            Replace image
          </OldButton>
        </div>
      )}
      <FileGallery
        active={active}
        onClose={closeFileGallery}
        filterOnFileType={"image"}
      />
    </Stack>
  )
}
